import React, { useMemo } from 'react';
import { Search, ChevronUp, ChevronDown } from 'lucide-react';
import type { Entity } from '../../types/api';
import { formatText, getKeysForType } from './helpers';
import ComponentTable from './ComponentTable';

interface ComponentTypeGroupProps {
  type: string;
  typeEntities: Entity[];
  isExpanded: boolean;
  typeSearch: string;
  onToggle: () => void;
  onSearchChange: (value: string) => void;
}

const ComponentTypeGroup: React.FC<ComponentTypeGroupProps> = ({
  type,
  typeEntities,
  isExpanded,
  typeSearch,
  onToggle,
  onSearchChange,
}) => {
  const filteredEntities = useMemo(() => {
    if (!typeSearch) return typeEntities;
    return typeEntities.filter(entity => {
      const name = entity.name || entity.entity_name || '';
      const metadata = entity.entity_metadata || entity.metadata || entity.properties || {};
      const metadataStr = JSON.stringify(metadata).toLowerCase();
      return (
        name.toLowerCase().includes(typeSearch.toLowerCase()) ||
        metadataStr.includes(typeSearch.toLowerCase())
      );
    });
  }, [typeEntities, typeSearch]);

  const { configKeys, metadataKeys } = useMemo(
    () => getKeysForType(filteredEntities),
    [filteredEntities]
  );
  const allKeys = [...configKeys, ...metadataKeys];

  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--color-surface-hover)] transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-engramiq-green" />
          ) : (
            <ChevronDown className="w-5 h-5 text-engramiq-green" />
          )}
          <span className="text-base font-bold eg-text-primary uppercase">
            {formatText(type)}
          </span>
          <span className="text-sm eg-text-secondary">
            ({typeEntities.length} nodes)
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-[var(--color-border)] p-4">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 eg-text-secondary" />
              <input
                type="text"
                placeholder="Search by component name or metadata..."
                value={typeSearch}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg eg-text-primary placeholder-[var(--color-text-secondary)] focus:outline-none focus:border-engramiq-green focus:ring-1 focus:ring-engramiq-green transition-all text-sm"
              />
            </div>
          </div>

          {filteredEntities.length === 0 ? (
            <div className="text-center py-6">
              <p className="eg-text-secondary text-sm">No nodes match the filter.</p>
            </div>
          ) : allKeys.length === 0 ? (
            <div className="space-y-2">
              <p className="eg-text-secondary text-sm mb-3">No properties or configuration found for these components.</p>
              {filteredEntities.slice(0, 50).map(entity => {
                const name = entity.name || entity.entity_name || 'Unknown';
                return (
                  <div key={entity.id || entity.entity_id} className="text-sm font-mono eg-text-primary">
                    {name}
                  </div>
                );
              })}
              {filteredEntities.length > 50 && (
                <p className="eg-text-secondary text-sm italic mt-3">
                  Showing first 50 of {filteredEntities.length} nodes.
                </p>
              )}
            </div>
          ) : (
            <ComponentTable
              entities={filteredEntities}
              configKeys={configKeys}
              metadataKeys={metadataKeys}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ComponentTypeGroup;
